import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const serif = { fontFamily: "'Playfair Display', Georgia, serif" };

export default function Login() {
  const { login, completeAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => new URLSearchParams(window.location.search).get("reset_token") ? "reset_complete" : "creds"); // creds | setup | verify | backup | reset | reset_complete
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [pendingUser, setPendingUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const goHome = (u) => navigate(u.role === "client" ? "/portal" : "/dashboard");

  const submitCreds = async (em, pw) => {
    setBusy(true); setError("");
    try {
      const data = await login(em, pw, "client");
      // If a firm user tried the client login, silently bounce them to /firm/login.
      // We preserve their email in the URL so they don't have to retype.
      if (data.wrong_context === "firm") {
        navigate(`/firm/login?email=${encodeURIComponent(em)}`, { replace: true });
        return;
      }
      setMfa(data);
      setCode("");
      setStep(data.mfa_setup_required ? "setup" : "verify");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const submitCode = async () => {
    setBusy(true); setError("");
    try {
      const endpoint = step === "setup" ? "/auth/mfa/enable" : "/auth/mfa/verify";
      const { data } = await api.post(endpoint, { mfa_token: mfa.mfa_token, code });
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
        setPendingUser(data);
        setStep("backup");
      } else {
        goHome(completeAuth(data));
      }
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const submitReset = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/password-reset", { email, code, new_password: newPassword });
      toast.success("Password reset — sign in with your new password");
      setPassword(""); setCode(""); setNewPassword("");
      setStep("creds");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const requestEmailLink = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      toast.success(data.message || "If that account exists, a reset link is on its way.");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const submitResetComplete = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/password-reset/complete", { token: resetToken, new_password: newPassword });
      toast.success("Password reset — sign in with your new password");
      setNewPassword("");
      navigate("/login", { replace: true });
      setStep("creds");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F6F3" }}>
      <div className="flex-1 flex items-center justify-center p-8" style={{ background: "#F7F6F3" }}>
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-2.5 mb-9">
            <img src="/artha-logo.png" alt="Artha" className="w-11 h-11 object-contain" />
            <div>
              <p className="font-medium text-sm leading-tight" style={{ color: "#0F2A5C" }}>Artha Business Solutions LLC</p>
              <p className="text-[11px]" style={{ color: "rgba(15,42,92,0.6)" }}>Tax Advisory &amp; Preparation · Client Portal</p>
            </div>
          </div>

          {step === "creds" && (
            <>
              <h1 className="text-2xl mb-1" style={{ ...serif, color: "#0F2A5C" }}>Welcome back</h1>
              <p className="text-sm mb-6" style={{ color: "rgba(15,42,92,0.7)" }}>Secure client &amp; firm portal · MFA enforced for all roles</p>
              <form onSubmit={(e) => { e.preventDefault(); submitCreds(email, password); }} className="space-y-3">
                <Input data-testid="login-email-input" type="email" placeholder="you@arthataxes.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <Input data-testid="login-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                {error && <p data-testid="login-error" className="text-xs text-rose-600">{error}</p>}
                <Button data-testid="login-submit-btn" type="submit" className="w-full" style={{ background: "#3D6B5E" }} disabled={busy}>{busy ? "Signing in…" : "Continue"}</Button>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setError(""); setStep("reset"); }} className="w-full text-xs transition-colors duration-150" style={{ color: "rgba(15,42,92,0.55)" }}>
                  Forgot password? Reset with your authenticator →
                </button>
                <button type="button" data-testid="firm-login-link" onClick={() => navigate("/firm/login")} className="w-full text-xs transition-colors duration-150" style={{ color: "rgba(15,42,92,0.65)" }}>
                  Firm staff? Sign in here →
                </button>
              </form>
            </>
          )}

          {step === "reset" && (
            <>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 mb-1 flex items-center gap-2"><ShieldCheck size={22} /> Reset password</h1>
              <p className="text-sm text-zinc-500 mb-6">Verify it's you with your authenticator app (or a backup code), then choose a new password. No email needed.</p>
              <form onSubmit={(e) => { e.preventDefault(); submitReset(); }} className="space-y-3">
                <Input data-testid="reset-email-input" type="email" placeholder="you@firm.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <Input data-testid="reset-code-input" placeholder="6-digit authenticator or backup code" value={code} onChange={(e) => setCode(e.target.value)} className="font-mono" required />
                <Input data-testid="reset-password-input" type="password" placeholder="New password (min 8 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                {error && <p data-testid="reset-error" className="text-xs text-rose-600">{error}</p>}
                <Button data-testid="reset-submit-btn" type="submit" className="w-full" disabled={busy || !email || code.length < 6 || newPassword.length < 8}>{busy ? "Resetting…" : "Reset password"}</Button>
                <div className="flex items-center gap-2 py-1">
                  <div className="h-px flex-1 bg-zinc-200" /><span className="text-[10px] text-zinc-400">OR</span><div className="h-px flex-1 bg-zinc-200" />
                </div>
                <Button data-testid="reset-email-link-btn" type="button" variant="outline" className="w-full" disabled={busy || !email} onClick={requestEmailLink}>
                  Email me a reset link instead
                </Button>
                <button type="button" data-testid="reset-back-btn" onClick={() => { setError(""); setStep("creds"); }} className="w-full text-xs text-zinc-400 hover:text-zinc-600">Back to sign in</button>
              </form>
            </>
          )}

          {step === "reset_complete" && (
            <>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 mb-1 flex items-center gap-2"><ShieldCheck size={22} /> Set a new password</h1>
              <p className="text-sm text-zinc-500 mb-6">Your reset link was verified — choose a new password below.</p>
              <form onSubmit={(e) => { e.preventDefault(); submitResetComplete(); }} className="space-y-3">
                <Input data-testid="reset-complete-password-input" type="password" placeholder="New password (min 8 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                {error && <p data-testid="reset-complete-error" className="text-xs text-rose-600">{error}</p>}
                <Button data-testid="reset-complete-submit-btn" type="submit" className="w-full" disabled={busy || newPassword.length < 8}>{busy ? "Saving…" : "Save new password"}</Button>
                <button type="button" onClick={() => { navigate("/login", { replace: true }); setStep("creds"); }} className="w-full text-xs text-zinc-400 hover:text-zinc-600">Back to sign in</button>
              </form>
            </>
          )}

          {step === "setup" && (
            <>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 mb-1 flex items-center gap-2"><ShieldCheck size={22} /> Set up MFA</h1>
              <p className="text-sm text-zinc-500 mb-4">Multi-factor authentication is required. Scan the QR code with Google Authenticator, 1Password, or Authy, then enter the 6-digit code.</p>
              <div className="rounded-lg border border-zinc-200 p-4 flex flex-col items-center mb-4">
                <img data-testid="mfa-qr-code" src={mfa?.qr_data_uri} alt="MFA QR code" className="w-44 h-44" />
                <button data-testid="mfa-copy-secret-btn" onClick={() => { navigator.clipboard?.writeText(mfa?.secret).catch(() => {}); toast.success("Secret copied"); }}
                  className="mt-2 flex items-center gap-1 font-mono text-[11px] text-zinc-500 hover:text-zinc-900 transition-colors duration-150">
                  <Copy size={12} /> {mfa?.secret}
                </button>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); submitCode(); }} className="space-y-3">
                <Input data-testid="mfa-code-input" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus className="font-mono text-center tracking-[0.4em]" />
                {error && <p data-testid="mfa-error" className="text-xs text-rose-600">{error}</p>}
                <Button data-testid="mfa-submit-btn" type="submit" className="w-full" disabled={busy || code.length < 6}>{busy ? "Verifying…" : "Enable MFA & sign in"}</Button>
                <button type="button" data-testid="mfa-back-btn" onClick={() => setStep("creds")} className="w-full text-xs text-zinc-400 hover:text-zinc-600">Back</button>
              </form>
            </>
          )}

          {step === "verify" && (
            <>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 mb-1 flex items-center gap-2"><ShieldCheck size={22} /> Two-factor code</h1>
              <p className="text-sm text-zinc-500 mb-6">Enter the 6-digit code from your authenticator app, or a backup code.</p>
              <form onSubmit={(e) => { e.preventDefault(); submitCode(); }} className="space-y-3">
                <Input data-testid="mfa-code-input" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} autoFocus className="font-mono text-center tracking-[0.4em]" />
                {error && <p data-testid="mfa-error" className="text-xs text-rose-600">{error}</p>}
                <Button data-testid="mfa-submit-btn" type="submit" className="w-full" disabled={busy || code.length < 6}>{busy ? "Verifying…" : "Verify & sign in"}</Button>
                <button type="button" data-testid="mfa-back-btn" onClick={() => setStep("creds")} className="w-full text-xs text-zinc-400 hover:text-zinc-600">Back</button>
              </form>
            </>
          )}

          {step === "backup" && (
            <>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 mb-1">Backup codes</h1>
              <p className="text-sm text-zinc-500 mb-4">Store these one-time codes safely. Each works once if you lose your authenticator.</p>
              <div data-testid="mfa-backup-codes" className="grid grid-cols-2 gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-4 mb-4">
                {backupCodes.map((c) => <span key={c} className="font-mono text-sm text-zinc-800">{c}</span>)}
              </div>
              <Button data-testid="mfa-backup-continue-btn" className="w-full" onClick={() => { navigator.clipboard?.writeText(backupCodes.join("\n")).catch(() => {}); goHome(completeAuth(pendingUser)); }}>
                Copy codes & continue
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:flex flex-1 relative flex-col justify-between p-12" style={{ background: "#0F2A5C" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center p-0.5">
            <img src="/artha-logo.png" alt="Artha" className="w-full h-full object-contain" />
          </div>
          <span className="text-sm font-medium text-white">Artha Business Solutions</span>
        </div>
        <div>
          <div style={{ width: 40, height: 2, background: "#3D6B5E", marginBottom: 24 }} />
          <p className="text-2xl leading-snug max-w-md" style={{ ...serif, color: "#fff" }}>
            Your accountant should know about your deal before your attorney does.
          </p>
          <p className="text-sm mt-4 max-w-md" style={{ color: "rgba(255,255,255,0.55)" }}>
            Proactive tax strategy, international compliance, and AI-powered finance — in one secure portal. AI drafts, CPAs approve, nothing ships without sign-off.
          </p>
          <div className="flex items-center gap-5 mt-8 text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
            <span>FBAR / FATCA Specialist</span>
            <span>·</span>
            <span>AICPA Member</span>
            <span>·</span>
            <span>Frisco, TX</span>
          </div>
        </div>
        <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.28)" }}>© 2025 Artha Business Solutions LLC · (469) 815-3491</p>
      </div>
    </div>
  );
}
